/**
 * Safe Area Provider
 * Uses native plugin for precise safe area insets on iOS and Android
 */

import { useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import SafeArea from '@/plugins/SafeAreaPlugin';
import { logger } from '@/lib/logger';
import { 
  SAFE_AREA_TOP_FALLBACK, 
  SAFE_AREA_BOTTOM_FALLBACK,
  ANDROID_STATUS_BAR_HEIGHT 
} from '@/lib/layout-constants';

interface SafeAreaInsets {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const useSafeArea = (): SafeAreaInsets => {
  const [insets, setInsets] = useState<SafeAreaInsets>({ 
    top: 0, 
    bottom: 0, 
    left: 0, 
    right: 0 
  });

  useEffect(() => {
    const platform = Capacitor.getPlatform();
    
    // Injetar CSS vars imediatamente com fallbacks para iOS
    if (platform === 'ios') {
      document.documentElement.style.setProperty('--sat', `${SAFE_AREA_TOP_FALLBACK}px`);
      document.documentElement.style.setProperty('--sab', `${SAFE_AREA_BOTTOM_FALLBACK}px`);
    }

    const loadInsets = async () => {
      // iOS ou Android nativo via Capacitor
      if (Capacitor.isNativePlatform()) {
        try {
          const result = await SafeArea.getInsets();
          
          // Validar se os valores são razoáveis (> 0 para iOS com notch)
          const validTop = result.top > 0 ? result.top : (platform === 'ios' ? SAFE_AREA_TOP_FALLBACK : ANDROID_STATUS_BAR_HEIGHT);
          const validBottom = result.bottom > 0 ? result.bottom : (platform === 'ios' ? SAFE_AREA_BOTTOM_FALLBACK : 0);
          
          const validInsets = {
            top: validTop,
            bottom: validBottom,
            left: result.left || 0,
            right: result.right || 0
          };
          
          logger.info('Safe area insets from native plugin', { platform, ...validInsets });
          setInsets(validInsets);
          
          // Injetar CSS vars globais
          document.documentElement.style.setProperty('--sat', `${validTop}px`);
          document.documentElement.style.setProperty('--sab', `${validBottom}px`);
          document.documentElement.style.setProperty('--sal', `${validInsets.left}px`);
          document.documentElement.style.setProperty('--sar', `${validInsets.right}px`);
          return;
        } catch (error) {
          logger.error('Failed to get safe area from native plugin, using fallback', error);
        }
      }
      
      // Fallback para web/PWA no iOS
      if (platform === 'ios') {
        const fallbackInsets = { top: SAFE_AREA_TOP_FALLBACK, bottom: SAFE_AREA_BOTTOM_FALLBACK, left: 0, right: 0 };
        setInsets(fallbackInsets);
        document.documentElement.style.setProperty('--sat', `${SAFE_AREA_TOP_FALLBACK}px`);
        document.documentElement.style.setProperty('--sab', `${SAFE_AREA_BOTTOM_FALLBACK}px`);
        logger.info('Using iOS fallback safe area', fallbackInsets);
        return;
      }
      
      // Android web fallback
      if (platform === 'android') {
        setInsets({ top: ANDROID_STATUS_BAR_HEIGHT, bottom: 0, left: 0, right: 0 });
        document.documentElement.style.setProperty('--sat', `${ANDROID_STATUS_BAR_HEIGHT}px`);
        document.documentElement.style.setProperty('--sab', '0px');
        logger.info('Using Android fallback safe area', { top: ANDROID_STATUS_BAR_HEIGHT });
      }
    };

    loadInsets();
    
    // Recalcular em resize (rotação, etc)
    window.addEventListener('resize', loadInsets);
    return () => window.removeEventListener('resize', loadInsets);
  }, []);

  return insets;
};
